import React from 'react';
import { Button } from '../../../catalyst/button';

export const StartVoyageModal = ({ 
  isOpen, 
  onClose, 
  voyageForm, 
  setVoyageForm, 
  passagePlans, 
  onSave 
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Prepare New Voyage</h3>
          <p className="text-sm text-gray-500 mt-1">Create voyage record - actual departure logged when "Let Go" is recorded</p>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Departure Port
            </label>
            <input
              type="text"
              value={voyageForm.departurePort}
              onChange={(e) => setVoyageForm({...voyageForm, departurePort: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="e.g., Portsmouth"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Destination Port
            </label>
            <input
              type="text"
              value={voyageForm.destinationPort}
              onChange={(e) => setVoyageForm({...voyageForm, destinationPort: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="e.g., Cowes"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Purpose
            </label>
            <select
              value={voyageForm.purpose}
              onChange={(e) => setVoyageForm({...voyageForm, purpose: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="commercial">Commercial</option>
              <option value="passenger">Passenger</option>
              <option value="training">Training</option>
              <option value="positioning">Positioning</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </div>
          {passagePlans.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Use Passage Plan (Optional)
              </label>
              <select
                value={voyageForm.passagePlanId}
                onChange={(e) => setVoyageForm({...voyageForm, passagePlanId: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">No passage plan</option>
                {passagePlans.map(plan => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={voyageForm.notes}
              onChange={(e) => setVoyageForm({...voyageForm, notes: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              rows={3}
              placeholder="Voyage notes, cargo details, etc."
            />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <Button color="white" onClick={onClose}>
            Cancel
          </Button>
          <Button color="indigo" onClick={onSave}>
            Create Voyage
          </Button>
        </div>
      </div>
    </div>
  );
};

export const EndVoyageModal = ({ 
  isOpen, 
  onClose, 
  endForm, 
  setEndForm, 
  activeVoyage,
  onSave 
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Complete Voyage Details</h3>
          <p className="text-sm text-gray-500 mt-1">Voyage ended with "All Fast" - add optional details below</p>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Arrival Port
            </label>
            <input
              type="text"
              value={endForm.arrivalPort}
              onChange={(e) => setEndForm({...endForm, arrivalPort: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder={activeVoyage?.destinationPort || 'Enter arrival port'}
            />
          </div>
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Optional Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Fuel Used (Litres)
                </label>
                <input
                  type="number"
                  value={endForm.fuelUsed}
                  onChange={(e) => setEndForm({...endForm, fuelUsed: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Engine Hours
                </label>
                <input
                  type="number"
                  value={endForm.engineHours}
                  onChange={(e) => setEndForm({...endForm, engineHours: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Optional"
                  step="0.1"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">These fields are optional for business tracking</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={endForm.notes}
              onChange={(e) => setEndForm({...endForm, notes: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              rows={3}
              placeholder="Any issues, delays, or notes about the voyage"
            />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <Button color="white" onClick={onClose}>
            Cancel
          </Button>
          <Button color="indigo" onClick={onSave}>
            Complete Voyage
          </Button>
        </div>
      </div>
    </div>
  );
};